import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { UserInfo } from '../../services/auth/authApi';
import { useUserInfo } from '../../hooks/useUserInfo';
import defaultProfileImage from '../../assets/images/profile.png';

interface SideNavigationBarProps {
  className?: string;
}

const SideNavigationBar: React.FC<SideNavigationBarProps> = ({ className }) => {
  const { userInfo } = useUserInfo();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { id: 'home', title: '커뮤니티 홈', path: '/community' },
    { id: 'questions', title: '강의 질문', path: '/community/lecture-questions' },
    { id: 'study', title: '스터디', path: '/community/study' },
    { id: 'info', title: '정보 공유', path: '/community/info-sharing' },
    { id: 'notice', title: '공지사항', path: '/community/notice' },
  ];

  const handleMenuClick = (path: string) => {
    navigate(path);
  };

  const handleProfileClick = () => {
    navigate('/mypage');
  };

  // 사용자 아이덴티티를 한글로 변환
  const getIdentityText = (identity: string) => {
    const identityMap = {
      'STUDENT': '의대생',
      'DOCTOR': '의사',
      'PROFESSOR': '교수',
      'OTHER': '기타'
    };
    return identityMap[identity as keyof typeof identityMap] || identity;
  };

  // 사용자 상세 정보 조합 (아이덴티티 · 소속)
  const getUserStatusText = (userInfo: UserInfo) => {
    const identityText = getIdentityText(userInfo.identity);
    let affiliation = '';
    
    // 아이덴티티에 따라 적절한 소속 정보 선택
    if (userInfo.identity === 'STUDENT' && userInfo.university) {
      // 의대생인 경우 대학교 표시
      affiliation = userInfo.university;
    } else if (userInfo.identity === 'DOCTOR' && userInfo.medicine) {
      // 의사인 경우 의료진 소속 (병원 등) 표시
      affiliation = userInfo.medicine;
    } else if (userInfo.identity === 'PROFESSOR') {
      // 교수인 경우 대학교 우선, 없으면 의료진 소속
      affiliation = userInfo.university || userInfo.medicine || '';
    } else {
      // 기타인 경우 또는 소속 정보가 없는 경우
      affiliation = userInfo.university || userInfo.medicine || '';
    }
    
    if (affiliation) {
      return `${identityText} · ${affiliation}`;
    }
    return identityText;
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Container className={className}>
      <SidebarContent>
        {/* 프로필 섹션 */}
        <ProfileSection>
          <ProfileContainer>
            <ProfileInfo onClick={handleProfileClick}>
              <ProfileImageContainer>
                <ProfileImageWrapper>
                  <ProfileImage 
                    src={userInfo?.image_url || defaultProfileImage} 
                    alt="프로필" 
                    onError={(e) => {
                      // 이미지 로드 실패 시 기본 이미지로 대체
                      const target = e.target as HTMLImageElement;
                      target.src = defaultProfileImage;
                    }}
                  />
                </ProfileImageWrapper>
              </ProfileImageContainer>
              <ProfileDetails>
                <ProfileName>{userInfo?.name || '게스트'}</ProfileName>
                <ProfileStatus>
                  {userInfo ? getUserStatusText(userInfo) : '의대생'}
                </ProfileStatus>
              </ProfileDetails>
            </ProfileInfo>
          </ProfileContainer>
        </ProfileSection>

        {/* 메뉴 섹션 */}
        <MenuSection>
          <MenuContainer>
            <MenuList>
              {menuItems.map((item) => (
                <MenuItem 
                  key={item.id} 
                  active={isActive(item.path)}
                  onClick={() => handleMenuClick(item.path)}
                >
                  <MenuItemContent>
                    <MenuItemTitle active={isActive(item.path)}>{item.title}</MenuItemTitle>
                    <ChevronIcon>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path 
                          d="M8.6 4.6L15.4 11.4C15.7 11.7 15.7 12.3 15.4 12.6L8.6 19.4" 
                          stroke={isActive(item.path) ? "#171719" : "rgba(55, 56, 60, 0.28)"} 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </ChevronIcon>
                  </MenuItemContent>
                </MenuItem>
              ))}
            </MenuList>
            
            {/* 글쓰기 버튼 */}
            <WriteButtonContainer>
              <WriteButton onClick={() => navigate('/community/write')}>
                <WriteButtonText>글쓰기</WriteButtonText>
              </WriteButton>
            </WriteButtonContainer>
          </MenuContainer>
        </MenuSection>
      </SidebarContent>
    </Container>
  );
};

const Container = styled.div`
  width: 242px;
  position: fixed;
  top: 80px;
  left: calc(50% - 600px); /* 1200px 컨테이너의 정확한 시작점 */
  height: fit-content;
  background: white;
  z-index: 10; /* 더 낮은 z-index */
  overflow: visible;
  
  @media (max-width: 1200px) {
    left: calc((100vw - 1200px) / 2);
  }
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const SidebarContent = styled.div`
  padding: 64px 0 128px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ProfileSection = styled.div`
  padding: 0;
`;

const ProfileContainer = styled.div`
  padding: 0;
`;

const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ProfileImageContainer = styled.div`
  display: flex;
  align-items: center;
  padding-right: 8px;
`;

const ProfileImageWrapper = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  outline: 1px solid rgba(112, 115, 124, 0.08);
  outline-offset: -1px;
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProfileDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProfileName = styled.div`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26.01px;
`;

const ProfileStatus = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.25px;
`;

const MenuSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MenuContainer = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MenuItem = styled.div<{ active: boolean }>`
  padding: 12px 12px 12px 16px;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
`;

const MenuItemContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MenuItemTitle = styled.div<{ active: boolean }>`
  color: ${props => props.active ? '#171719' : 'rgba(55, 56, 60, 0.28)'};
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
`;

const ChevronIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WriteButtonContainer = styled.div`
  padding: 12px 8px 0 8px;
`;

const WriteButton = styled.button`
  width: 100%;
  padding: 12px 28px;
  background: #448181;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #3a6f6f;
  }
`;

const WriteButtonText = styled.div`
  color: white;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26.01px;
`;

export default SideNavigationBar;